import { estadoDoEvento, formatarNoFuso, fusoDoNavegador } from '@vestigo/core/area-agenda';
import { useLocale, useT } from '../lib/idioma.js';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { chamadaDoTempo } from '../agenda/peso-do-encontro.js';
import { api, type EventoDaAgenda } from '../lib/api.js';
import { caminhoDaArea } from '../lib/endereco.js';

/*
 * A pintura de uma porta, num sítio só.
 *
 * Ela saiu de dentro de `Porta` quando `Trilhas` passou a ser âncora OU `<Link>`
 * conforme a página: seriam três cópias da mesma aparência, e *três cópias de
 * uma aparência são três maneiras de um item desta barra ficar diferente dos
 * vizinhos sem ninguém reparar.*
 *
 * **Em 06/10/2026 a âncora morreu e o caller voltou a ser um só** (`Porta`) — e
 * a constante fica na mesma, porque o que ela guarda não era a duplicação: são
 * os **44 px de altura do alvo**, a única parte disto que não é estética. O que
 * se vê é discreto; o que se clica não é. Um número com nome é um número que
 * alguém procura antes de o mudar.
 */
const PINTURA_DA_PORTA =
  'inline-flex min-h-[44px] shrink-0 items-center rounded-md px-3 py-2 text-sm ' +
  'transition-colors hover:bg-[color-mix(in_srgb,var(--texto-suave)_16%,transparent)]';

/**
 * As portas da área — agenda, mural e trilhas — e a faixa da live.
 *
 * ## Por que as portas subiram para o cabeçalho
 *
 * Elas ocupavam **uma linha inteira** logo abaixo do nome da área. Três links
 * numa faixa própria, e essa faixa empurrava o herói, a saudação e as
 * prateleiras para baixo — custava ~60px da primeira tela para dizer o que cabia
 * ao lado do nome. Agora elas moram na mesma linha do nome da comunidade, que é
 * onde a pessoa já está olhando quando se pergunta "o que mais tem aqui?".
 *
 * ## Por que elas continuam fora do menu do dono
 *
 * O menu do cabeçalho é dele — até seis links que ele escolheu (suporte, o grupo
 * do WhatsApp, a página de vendas). As portas são do PRODUTO: existem em toda
 * área e não disputam a escolha de ninguém. Ficam antes do menu, com peso visual
 * próprio, e o menu continua sendo a lista dele.
 *
 * ## A faixa da live subiu para o cabeçalho — e a tese que a impedia caiu
 *
 * Aqui esteve escrito, até 25/09/2026:
 *
 * > *«A faixa da live continua sendo a primeira coisa da página. Ela **não**
 * > subiu junto, e de propósito: é a única coisa da vitrine com prazo — em uma
 * > hora não existe mais — e por isso vale uma faixa larga com o botão de
 * > entrar. Enfiá-la no cabeçalho a reduziria a um ponto colorido ao lado de
 * > `Agenda`, que é o tamanho de algo que não é urgente.»*
 *
 * **Derrubada por ordem do dono de 25/09/2026 (agente uiux):** *«essa
 * notificação toma muito espaço, coloque ao lado esquerdo de procurar ou do lado
 * de trilhas na barra superior»*. A tese tinha uma falha de aritmética que só o
 * relógio revela: ela defendia a largura inteira pelo estado **a decorrer**, que
 * dura ~3 horas por mês, e cobrava-a nas ~700 em que o encontro só está
 * marcado. E o substituto **não é um ponto colorido**: é a `PastilhaDoEncontro`
 * — caixa, palavra, hora com sigla, respiro e rotação —, a única peça daquela
 * barra que tem contorno. O cabeçalho não cresceu um pixel por ela, e a página
 * devolveu 76 px em 1440 e 189 px em 390. Ver o cabeçalho dela, mais abaixo.
 */

/** A agenda da área, buscada uma vez e lida pelas duas peças. */
function useEventos(areaSlug: string): EventoDaAgenda[] {
  const [eventos, setEventos] = useState<EventoDaAgenda[] | null>(null);

  useEffect(() => {
    let vivo = true;
    void api
      .get<{ eventos: EventoDaAgenda[] }>(`/a/${areaSlug}/agenda`)
      /* Falhar aqui é silencioso: a vitrine é o que a pessoa veio ver, e uma
         faixa de agenda que não carregou não pode levá-la junto. */
      .then((r) => vivo && setEventos(r.eventos))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [areaSlug]);

  return (eventos ?? []).filter((e) => e.status === 'published');
}

/** Um relógio de um minuto — é a granularidade de "falta 1 h" e de "agora". */
function useAgora(): Date {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return agora;
}

/**
 * As três portas, para o cabeçalho.
 *
 * `Trilhas` só aparece quando existe alguma: uma porta que abre para uma lista
 * vazia é uma porta que ensina a não abrir portas. Quem decide é a vitrine, que
 * já tem as trilhas em mãos.
 */
export function PortasDaArea({
  areaSlug,
  temTrilhas = false,
  comunidadeAberta,
  cracha = null,
}: {
  areaSlug: string;
  temTrilhas?: boolean;
  /**
   * ── A PORTA DA COMUNIDADE FECHA (0432) ────────────────────────────────────
   *
   * Este item era o ÚNICO desta barra sem condição nenhuma — `Trilhas` tem
   * `temTrilhas &&` vinte linhas abaixo, e a comunidade não tinha nada, porque
   * até a 0432 não havia interruptor nenhum dela em todo o produto.
   *
   * A tranca de verdade é o `preHandler` de `aluno-comunidade.ts`, que responde
   * **404**. Este booleano é o que impede a pessoa de bater nela: *«porta que
   * abre para lista vazia ensina a não abrir portas»* é a regra que este
   * ficheiro já escreve para as trilhas, e um 404 é pior que uma lista vazia.
   *
   * **`undefined` é «não sei», e «não sei» NÃO esconde.** É o padrão, e é o que
   * faz a prévia do Estúdio — que monta esta barra sem servidor — e um corpo de
   * resposta antigo continuarem a desenhar a porta. Esconder no «não sei»
   * repetiria, no navegador, o defeito que o telemóvel já pagou: um 403 de termos
   * apagava a aba de uma escola que tinha comunidade, e a tela afirmava que a
   * escola não tinha. A lição está em `apps/app/src/lib/veredito-da-comunidade.ts`
   * e vale igual aqui: **só o «não» explícito fecha.**
   */
  comunidadeAberta?: boolean;
  /**
   * ── «PERGUNTARAM POR ELE» (`PER-03`), E A LISTA FECHADA (`J1299` c6) ─────
   *
   * O número de avisos dirigidos a esta pessoa — respostas às perguntas dela,
   * menções, respostas dela marcadas como a que resolveu — e **a frase que diz
   * o que esse número conta**, as duas feitas no servidor. Vem da vitrine, na
   * mesma ida: a porta não paga um pedido por uma luz, e o pedaço de entrada
   * não paga as palavras.
   *
   * **`null` é o padrão e desenha NADA** (`PER-04`). O `= null` aqui não é
   * conveniência: é o que faz a prévia do Estúdio, que monta esta barra sem
   * servidor, nascer sem crachá em vez de nascer com um crachá indefinido.
   */
  cracha?: { quantos: number; rotulo: string } | null;
}) {
  const t = useT();
  const eventos = useEventos(areaSlug);
  const agora = useAgora();

  /*
   * `naVitrine` morreu aqui em 06/10/2026, com o `useLocation` e o
   * `semBarraFinal` que o calculavam. Eles respondiam a uma pergunta só — «este
   * item é `<a href="#trilhas">` ou `<Link>`?» — e a âncora deixou de existir.
   * Ver o item `Trilhas`, mais abaixo, para a ordem que a tirou.
   */
  const meuFuso = useMemo(() => fusoDoNavegador(), []);

  /*
   * ── O PONTO DA `Agenda` É O SINAL DO TELEMÓVEL (25/09/2026) ───────────────
   *
   * Ele dizia só «acontecendo agora». Abaixo de 1280 px a pastilha do encontro
   * não cabe nesta barra — a medição está no cabeçalho de `PastilhaDoEncontro`
   * —, e este ponto passa a ser o único sinal que um telefone tem. Então ele
   * acende pela MESMA régua do respiro dela: `dias === null`, o conjunto {a
   * decorrer, entrada aberta, hoje, é amanhã}.
   *
   * **Nem mais, nem menos.** Acender por `faltam 5 dias` deixaria o ponto aceso
   * o mês inteiro numa área com um ou dois encontros por mês, e um sinal sempre
   * aceso não é um sinal; acender só por «a decorrer», como antes, deixaria o
   * telemóvel sem aviso nenhum nas ~700 horas por mês em que não há live.
   *
   * `xl:hidden` aqui e `xl:inline-flex` na pastilha: **nunca se vêem os dois ao
   * mesmo tempo**, e o que o ponto cala, ela imprime.
   */
  const primeiro = emCartaz(eventos, agora)[0];
  const sinal = primeiro ? chamadaDoTempo(primeiro, agora, meuFuso, t) : null;
  const acende = !!sinal && sinal.dias === null;

  return (
    <nav aria-label={t('nav.secoesDaArea')} className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <Porta para={caminhoDaArea(areaSlug, `/agenda`)}>
        Agenda
        {/* O ponto diz «há coisa marcada e é já» sem tentar contar o que é —
            contar é da pastilha, que aparece a partir de `md`. Ver acima. */}
        {acende && primeiro && sinal && (
          <Respiro
            perto
            rotulo={`Encontro ${sinal.palavra}: ${primeiro.title}`}
            extra="ml-1.5 inline-block align-middle xl:hidden"
          />
        )}
      </Porta>
      {/* `!== false`: ver `comunidadeAberta`. «Não sei» desenha. */}
      {comunidadeAberta !== false && (
      <Porta para={caminhoDaArea(areaSlug, `/comunidade`)}>
        {t('nav.comunidade')}
        {/*
         * ── O CRACHÁ, E ELE SÓ EXISTE QUANDO HÁ FACTO (`PER-03`, `PER-04`) ──
         *
         * O item «Comunidade» estava **nu**: sem crachá, sem contador, sem
         * ponto. A porta é o único sítio do produto por onde toda a gente
         * passa, e era o único que não sabia que alguém tinha respondido.
         *
         * **`> 0` e não `>= 0`.** Um crachá desenhado sem condição acende para
         * quem não tem nada, e é o alvo plantado desta linha: a tela fabricar
         * um zero é a doença desta casa, e um «0» ao lado de «Comunidade» diz
         * à pessoa, todos os dias, que ninguém falou com ela.
         *
         * **É um NÚMERO, e o ponto ao lado de «Agenda» é um PONTO.** As duas
         * luzes partilham `--destaque` porque a área tem uma cor de ênfase só,
         * e distinguem-se pela FORMA: a agenda diz «está a acontecer» (não há
         * o que contar), esta diz «há N coisas dirigidas a si».
         *
         * O rótulo acessível leva o número **exacto**, mesmo quando a pastilha
         * corta em `9+`: quem ouve a tela não tem como abrir a pastilha para
         * ver o resto.
         *
         * **E o rótulo diz O QUE conta** (`J1299` c6). «4 respostas para você»
         * sobre um crachá que conta PERGUNTAS, ou sobre uma menção, era a tela a
         * afirmar o que não é verdade. A frase chega pronta, com as partes que
         * somam o número; o `title` dá-a também a quem vê e passa o rato.
         */}
        {cracha && cracha.quantos > 0 && (
          <span
            data-forma="cracha-da-comunidade"
            aria-label={cracha.rotulo}
            title={cracha.rotulo}
            className="ml-1.5 inline-flex min-w-[18px] items-center justify-center px-1 align-middle text-[11px] font-semibold leading-[16px] tabular-nums"
            style={{
              background: 'var(--destaque)',
              color: 'var(--sobre-destaque, #08130c)',
              borderRadius: '999px',
            }}
          >
            {cracha.quantos > 9 ? '9+' : cracha.quantos}
          </span>
        )}
      </Porta>
      )}
      {/*
       * `Minhas aulas` aparece SEMPRE, inclusive vazia — ao contrário de
       * `Trilhas`.
       *
       * A regra "porta que abre para lista vazia ensina a não abrir portas" vale
       * para o catálogo do dono: uma seção sem conteúdo é promessa dele que ele
       * não cumpriu. Esta lista é da PESSOA, e ela só ganha conteúdo depois de
       * alguém descobrir que ela existe. Escondê-la enquanto está vazia é
       * garantir que fique vazia — e é o que fazia `♥ Favoritar` gravar num
       * lugar sem tela. A lista vazia aqui explica para que servem as duas.
       */}
      <Porta para={caminhoDaArea(areaSlug, `/minhas-aulas`)}>{t('nav.minhasAulas')}</Porta>
      {/*
       * ═══ `Trilhas` DEIXA DE SER ÂNCORA E PASSA A SER ROTA (06/10/2026) ═════
       *
       * **A história, porque ela é a lição.** Em 01/10 o dono mediu o defeito:
       * *«o link de trilhas da barra de menu de agenda deve apontar para a
       * trilha na vitrine, ele tá link morto hoje»*. Este item era
       * `href="#trilhas"` fixo, as trilhas eram uma seção DA VITRINE, e fora
       * dela o navegador procurava um id que não existia naquela página — nem
       * navegava, nem avisava, nem rolava. O conserto daquele dia foi tornar o
       * item âncora NA vitrine e `<Link>` para `<vitrine>#trilhas` fora dela.
       *
       * **Em 06/10 o dono tirou a seção:** *«na vitrine sai o espaço especial de
       * trilhas já que ela terá sua própria prateleira ou prateleiras definidas
       * pelo tutor»*. `secoesDaVitrine` já não emite `trilhas` (`0467`), logo
       * `#trilhas` não existe em página NENHUMA — e o conserto de 01/10 passou a
       * estar morto dos dois lados, inclusive na vitrine, onde era o lado que
       * «funcionava».
       *
       * *Uma âncora relativa é uma promessa sobre a página em que se está* — e
       * desta vez a página deixou de ter o que prometer. O destino é uma ROTA.
       *
       * ── POR QUE UMA ROTA, E NÃO `#prateleira-<id>` ────────────────────────
       *
       * Porque a trilha vive agora numa prateleira que o tutor dispõe, e o id
       * dela é um **uuid**: apontar para lá poria um identificador interno na
       * URL de toda a gente e morreria no dia em que o tutor renomeasse ou
       * apagasse a faixa. Seria o mesmo defeito de 01/10 com outro `#`, e sem o
       * aviso — porque desta vez o id existiria, só não seria o certo.
       *
       * ── E O QUE DESAPARECEU DAQUI COM A MUDANÇA ───────────────────────────
       *
       * `useLocation`, o `semBarraFinal` e o `naVitrine`. Eles existiam **só**
       * para escolher entre `<a>` e `<Link>`, e a razão de a decisão ser tomada
       * aqui em vez de numa `prop` — *«a tela que se esquecesse voltaria ao link
       * morto, em silêncio»* — deixa de ter o que decidir: `Porta` é um `<Link>`
       * e um `<Link>` vale igual nas cinco telas. Um item desta barra passa a ser
       * indistinguível dos vizinhos, que é o que a `PINTURA_DA_PORTA` sempre
       * quis e nunca teve.
       */}
      {temTrilhas && <Porta para={caminhoDaArea(areaSlug, `/trilhas`)}>{t('nav.trilhas')}</Porta>}
    </nav>
  );
}

/**
 * Uma porta.
 *
 * Sem fundo em repouso e com fundo ao passar o mouse: em repouso elas eram três
 * blocos cinzas competindo com o nome da área ao lado, e o nome é quem manda
 * naquela linha. O alvo continua com 44px de altura mesmo sem o fundo aparecer —
 * o que se vê é discreto, o que se clica não é.
 */
function Porta({ para, children }: { para: string; children: React.ReactNode }) {
  return (
    <Link to={para} className={PINTURA_DA_PORTA} style={{ color: 'var(--texto-suave)' }}>
      {children}
    </Link>
  );
}

/**
 * O ponto que respira — a MESMA peça no ponto da porta e dentro da pastilha.
 *
 * O respiro é uma classe de FOLHA (`vst-respira`, no `index.css`) e não um
 * `style`: `@media (prefers-reduced-motion: reduce)` só existe numa folha, e uma
 * propriedade em linha não sabe da preferência de quem pediu que nada se mexa.
 * A classe só é EMITIDA quando o encontro está perto — o bloco `reduce` é a
 * segunda tranca, para o dia em que alguém mude esta linha sem ler aquela.
 *
 * `rotulo` presente = este ponto é o único sinal que aquela largura tem (o
 * telemóvel), e então ele fala: `role="img"` com nome, porque um `aria-label`
 * num `<span>` sem papel não é exposto por toda tecnologia de apoio. Dentro da
 * pastilha ele é `aria-hidden`, e de propósito: a palavra está ao lado dele, e
 * um leitor de tela que anuncie as duas coisas diz a mesma coisa duas vezes.
 */
function Respiro({
  perto,
  rotulo = '',
  extra = '',
}: {
  perto: boolean;
  rotulo?: string;
  extra?: string;
}) {
  return (
    <span
      role={rotulo ? 'img' : undefined}
      aria-hidden={rotulo ? undefined : true}
      aria-label={rotulo || undefined}
      title={rotulo || undefined}
      data-forma="respiro-do-encontro"
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${extra} ${perto ? 'vst-respira' : ''}`}
      /* O acento do cliente, misturado com o texto — `--destaque` puro chegou a
         1,90:1 contra o fundo numa paleta possível, e um respiro invisível é um
         respiro que não existe. */
      style={{ background: 'color-mix(in srgb, var(--destaque) 60%, var(--texto))' }}
    />
  );
}

/**
 * Os encontros em cartaz, em ordem — a MESMA lista para o ponto da porta e para
 * a pastilha, e uma só conta em vez das duas que este ficheiro tinha.
 */
function emCartaz(eventos: EventoDaAgenda[], agora: Date): EventoDaAgenda[] {
  const rolando = eventos.find(
    (e) =>
      estadoDoEvento(
        { startsAt: new Date(e.startsAt), durationMinutes: e.durationMinutes },
        agora,
      ) === 'agora',
  );
  /* O que está a decorrer manda sozinho — ver a tabela da rotação. */
  if (rolando) return [rolando];
  /* `startsAt` é ISO-8601 em UTC, e nesse formato a ordem alfabética É a ordem
     cronológica. É a linha que a faixa já usava antes desta leva. */
  return eventos
    .filter((e) => new Date(e.startsAt).getTime() > agora.getTime())
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 * ── A PASTILHA DO ENCONTRO — NA BARRA, À ESQUERDA DE `Procurar` ──────────────
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **A ordem do dono, 25/09/2026** — a segunda sobre esta peça no mesmo dia,
 * sobre a faixa que a primeira mandou crescer:
 *
 * > *«essa notificação toma muito espaço, coloque ao lado esquerdo de procurar
 * > ou do lado de trilhas na barra superior com uma animação de respiro, uma
 * > caixa menor, e que fica rolando se tiver mais de um»*
 *
 * Isto **não revoga** a ordem anterior (*«ele merece um outro espaço»*), e é
 * importante não a ler assim: a linha de 13 px em `--texto-suave` que existia
 * antes de 25/09 não volta. O que muda é o EIXO em que o encontro ganha peso.
 * Ele era caro em **altura** — uma faixa de largura inteira, 76 px em 1440 e
 * 189 px em 390, a empurrar o herói da área para baixo em toda visita, para
 * dizer uma coisa que muda uma ou duas vezes por mês. Passa a ser caro em
 * **permanência e em movimento**: uma pastilha de 26 px na barra de cima, a
 * única peça daquela barra que tem caixa, e a única que se mexe.
 *
 * | | a faixa (a manhã de 25/09) | a pastilha (a tarde) |
 * |---|---|---|
 * | altura que cobra à página | **76 px** / **189 px** | **0 px** — ver abaixo |
 * | onde | abaixo do cabeçalho, largura inteira | dentro do cabeçalho, à esquerda de `Procurar` |
 * | o que a distingue do que está ao lado | o tamanho | **ter caixa**, e respirar |
 * | com dois encontros marcados | mostrava um e calava o outro | **roda entre eles** |
 *
 * ## O 0 px não é figura de estilo, e é o que faz esta leva caber
 *
 * Todo controlo daquela barra já tem 44 px de altura de alvo — a lei `C2` desta
 * casa, escrita em `ALVO`, no `Cabecalho`. A altura do cabeçalho é `py-3`
 * (24 px) mais o filho mais alto, e o filho mais alto é 44. **A pastilha entra
 * com um alvo de exactamente 44 px**, e por isso a barra não cresce um pixel.
 * A caixa que se VÊ tem 26 px e vive dentro desse alvo, como o `←` de `voltar`
 * vive dentro do dele. *Medido no Chromium, nos três temas — ver
 * `agenda/ver-a-faixa.ts`, que escreve os números.*
 *
 * ## Por que à esquerda de `Procurar`, e não ao lado de `Trilhas`
 *
 * O dono deu os dois sítios. `Trilhas` mora dentro do `<nav aria-label="Seções
 * da área">`, que é `overflow-x-auto` e que **a 390 px já é um rolador
 * horizontal parado no zero**: uma pastilha acrescentada lá fica depois da borda
 * direita no telemóvel, alcançável só por um arrasto que ninguém faz. *A única
 * coisa que esta peça não pode ser é invisível.* E aquele nav é uma lista de
 * quatro destinos que estão sempre lá; a pastilha é um FACTO com prazo, que na
 * maior parte dos dias não existe — fazê-la o quinto item ensina que um dos
 * cinco se comporta ao contrário dos outros quatro (mexe-se, desaparece, e às
 * vezes aponta para fora da aplicação).
 *
 * À esquerda de `Procurar` ela está **fora** do rolador, é `shrink-0`, e é a
 * casa que toda barra desta classe dá ao estado.
 *
 * ## ⚠ ABAIXO DE 1280 px ELA NÃO DESENHA — E O NÚMERO FOI MEDIDO, NÃO ESCOLHIDO
 *
 * A regra que decide é uma só: **não se troca navegação por notificação.** A
 * pastilha é `shrink-0`, e num flex a largura dela sai de um sítio — o rolador
 * das portas, que é quem tem `min-w-0`. A pergunta não é «cabe?»: é «o que é que
 * deixa de se ver para ela caber?».
 *
 * Medido no Chromium com o cabeçalho INTEIRO montado — nome, as quatro portas,
 * a busca, `Suporte`, `Áreas`, `Conta`, `Sair` — por `agenda/ver-a-faixa.ts`, na
 * cena mais larga (`ACONTECENDO AGORA · Entrar`, 209 px):
 *
 * | largura | portas SEM pastilha | portas COM | as quatro veem-se? | transborda? |
 * |---|---|---|---|---|
 * | 1440 | 750 | 529 | **sim** | não |
 * | 1366 | 676 | 455 | **sim** | não |
 * | **1280** | 590 | **369** | **sim** — a última largura em que sim | não |
 * | 1152 | 462 (inteiras) | 241 | **não: a pastilha É QUE AS CORTA** | não |
 * | 1024 | 334 (já cortadas) | 113 | não — e piora de «Trilhas» para «Comunidade» | não |
 * | 768 | 78 | — | — | **sim, por 120 px** |
 *
 * **1152 é a linha que condena `lg`.** Ali as quatro portas veem-se inteiras sem
 * a pastilha e deixam de se ver com ela: o custo sai todo da navegação. Em 1024
 * o rolador já cortava antes desta leva — a pastilha não cria o defeito, mas
 * agrava-o de «`Trilhas` a meio» para «`Comunidade` a meio». E em 768 o que cede
 * deixa de ser o rolador e passa a ser a janela, que é o defeito de 497 px que
 * esta barra já pagou uma vez.
 *
 * `xl` é **a maior largura em que a pastilha não custa nada a ninguém**, e é por
 * isso que é ela. Abaixo, a pastilha não desenha e o sinal é o **ponto na porta
 * da `Agenda`** — que já existia para o encontro a decorrer, que está no PRIMEIRO
 * item do rolador (logo, visível sem arrastar nada), e que agora respira pela
 * mesma régua. O ponto é `xl:hidden` e a pastilha é `xl:inline-flex`: **nunca se
 * veem os dois**, e nunca se ve nenhum.
 *
 * **E no telemóvel isto é melhor do que o que estava no ar esta manhã:** a faixa
 * cobrava 189 px de um ecrã de 844 — 22 % da primeira tela — para dizer o que o
 * ponto diz de graça, e a medição mostra que a 390 px as portas ficam com os
 * MESMOS 90 px com pastilha e sem ela. O que se perde abaixo de 1280 é o
 * `faltam N dias` impresso: ele vive na agenda, a um toque, e no `title` do
 * ponto. *Dito às claras para não se descobrir por acaso.*
 *
 * ## Ela chega por `prop`, e isso poupa dez pedidos de rede
 *
 * O `Cabecalho` monta em onze rotas. Se a pastilha vivesse lá dentro, as onze
 * passariam a pedir `/a/:slug/agenda`. Vem como `React.ReactNode`, pelo mesmo
 * caminho que `portas` já usava, e quem a monta é quem já montava a faixa: a
 * vitrine. *Nenhum pedido novo, em nenhuma rota.*
 *
 * ## O RESPIRO, e as três coisas que o tornam suportável
 *
 * Movimento perpétuo ao lado de um campo de busca é um imposto de atenção que
 * se paga todos os dias. Três travões, e os três são declarados:
 *
 * 1. **Respira o PONTO, não a pastilha.** Um ponto de 6 px, em opacidade e
 *    escala (`1 → 0,4 → 1`). Nada reflui, nenhum texto muda de nitidez, e o
 *    movimento fica na visão periférica em vez de onde se lê.
 * 2. **O ciclo é longo, e o respiro ACABA.** 3,6 s por volta e seis voltas —
 *    ~22 segundos, o tempo de um relance numa página que se acabou de abrir.
 *    Depois disso o ponto é um ponto e a pastilha é mobília. Quem volta à página
 *    respira outra vez, que é o que se quer: o trabalho do respiro é *ser
 *    notado uma vez, à chegada.*
 * 3. **Só respira o que está perto.** `hoje`, `é amanhã`, a entrada aberta e o
 *    que está a decorrer. `faltam 5 dias` **não respira** — um ponto a pulsar
 *    por algo a cinco dias é a urgência fabricada que a régua desta casa recusa
 *    por escrito, e numa área com um ou dois encontros por mês seria um ponto a
 *    pulsar trezentos e sessenta dias por ano.
 *
 * O predicado é `chamada.dias === null`, e **não é novo**: `dias` só é número no
 * degrau `faltam N dias` (ver `peso-do-encontro.ts`), logo `null` é exactamente
 * o conjunto {a decorrer, entrada aberta, hoje, é amanhã}. **A mesma bandeira
 * decide duas coisas** — o respiro aqui, e o ponto que acende no telemóvel
 * (`PortasDaArea`, mais acima). Uma ideia dita em dois sítios não tem como se
 * contradizer consigo.
 *
 * ## A ROTAÇÃO, e por que quase nunca acontece
 *
 * A densidade medida desta casa é **um a dois encontros por mês**. Com um só
 * encontro **não há temporizador nenhum** — nem intervalo, nem `key` a mudar,
 * nem classe de transição. É o caso comum, e é o caso parado.
 *
 * Com dois ou três: **7 s cada**, com um dissolve de 220 ms na troca. Sete
 * segundos porque isto é ambiente e não um carrossel que alguém está a ver; o
 * dissolve porque sem ele o texto «muda de ideias», e com ele lê-se como um
 * mostrador que roda — *é o dissolve que diz que há mais do que um*, e por isso
 * não há um contador `1/3` a comer a caixa.
 *
 * | | |
 * |---|---|
 * | pára com o rato em cima | sim (`onPointerEnter`) |
 * | pára com o foco do teclado | sim (`onFocus`) — não se lê um alvo em movimento |
 * | o teclado alcança os outros | **pela agenda**: a pastilha é UM link e o destino é a lista inteira. A rotação é uma prévia, nunca o único caminho. |
 * | quantos rodam | no máximo **3**: rodar oito a 7 s é um ciclo de um minuto cujo fim ninguém vê. O rótulo diz o total verdadeiro. |
 * | há algo a decorrer | **não roda** — o que está a acontecer é o único assunto, e alternar «acontecendo agora» com «faltam 12 dias» enterra o primeiro. |
 *
 * Não há `aria-live`: um estado que se anuncia a cada sete segundos a quem ouve
 * a tela é um instrumento de tortura. O rótulo acessível — que é também o
 * `title` de quem passa o rato — leva o **título do encontro** (que a caixa não
 * imprime), a chamada do tempo, a data, a hora com a sigla, e quantos mais há.
 *
 * ## O que a caixa IMPRIME, e o que ela cala
 *
 * O título sai. É o único facto aqui de comprimento ilimitado — e o menos
 * decisivo: com um ou dois encontros por mês, a pessoa sabe qual é. Ele fica
 * **alcançável** no rótulo e no `title`, e escrito por inteiro na agenda, que é
 * onde a pastilha leva.
 *
 * **A data também sai, e essa foi uma decisão de medição.** A primeira versão
 * imprimia `FALTAM 5 DIAS · 30/09 22:00 BRT`, e o Chromium deu-lhe **210 px**
 * contra os **156** da mesma pastilha sem a data — 54 px, na largura mais
 * apertada em que ela existe, para dizer o dia uma segunda vez. *`faltam 5 dias`
 * JÁ diz que dia é.* A data por extenso fica no rótulo, no `title` e na agenda.
 *
 * | o degrau | a caixa diz |
 * |---|---|
 * | a decorrer / entrada aberta, com link | `ACONTECENDO AGORA · Entrar` — hora nenhuma: a hora de algo que está a acontecer não é informação |
 * | `hoje` · `é amanhã` | `HOJE · 19:00 BRT` |
 * | `faltam N dias` | `FALTAM 5 DIAS · 19:00 BRT` |
 *
 * **A lei do fuso sobrevive inteira:** a sigla está colada à hora e nunca se
 * separa dela — as duas saem juntas ou ficam juntas. Abaixo de 1024 px saem as
 * duas, e nunca `19:00` sem referência.
 *
 * ## A cor não é o que distingue esta pastilha
 *
 * `--destaque` é do cliente e chegou a **1,90:1** contra o fundo numa paleta
 * possível — medido nesta casa, no tema azul-marinho da bancada. O que separa a
 * pastilha dos controlos ao lado é **geometria**: ela é a única daquela barra
 * que tem caixa (um fio de `--texto` a 32 % sobre uma superfície de `--texto` a
 * 8 %). O ponto toma o acento do cliente misturado com o texto
 * (`60/40`), como o anel da faixa já fazia, para existir em qualquer paleta — e
 * mesmo que ele se apague não se perde facto nenhum: **o ponto é redundante com
 * a palavra que está ao lado dele.**
 *
 * O peso pleno engrossa o fio para 1,5 px e acrescenta o verbo. *É o fio e a
 * palavra que sobem o peso, não a cor.*
 *
 * ## O que esta pastilha deixou de ter face à faixa — dito às claras
 *
 * O botão cheio de `Entrar agora`. No estado a decorrer a faixa dava um alvo de
 * 44 px preenchido com o acento; a pastilha dá `· Entrar` dentro de uma caixa de
 * fio grosso. Em troca, o estado a decorrer existe cerca de **três horas por
 * mês**, contra as setecentas do estado sereno: optimizou-se o caso de sempre.
 * Se o dono sentir a falta do botão cheio, o gesto é reinstalar a faixa **só**
 * no peso pleno — e não desfazer isto.
 */
export function PastilhaDoEncontro({ areaSlug }: { areaSlug: string }) {
  const locale = useLocale();
  const t = useT();
  const eventos = useEventos(areaSlug);
  const agora = useAgora();
  const meuFuso = useMemo(() => fusoDoNavegador(), []);
  /** Qual dos que rodam está em cartaz. Cresce sem teto; quem fecha é o `%`. */
  const [volta, setVolta] = useState(0);
  /** O rato em cima, ou o foco do teclado dentro. Enquanto for `true`, para. */
  const [parado, setParado] = useState(false);

  const todos = emCartaz(eventos, agora);
  /* Três, e não todos: ver a tabela da rotação. O rótulo conta `todos`. */
  const lista = todos.slice(0, 3);

  /*
   * ── O TEMPORIZADOR NASCE COM DOIS, E NÃO NASCE COM UM ────────────────────
   *
   * `lista.length < 2` sai antes de criar coisa nenhuma: com um encontro — o
   * caso comum desta casa — não há `setInterval` a correr por baixo da página.
   * `parado` nas dependências volta a armar o intervalo quando o rato sai, o que
   * dá ao item em cartaz sete segundos inteiros a partir daí, e não o resto de
   * um relógio que continuou a andar por trás da mão de alguém.
   */
  useEffect(() => {
    if (lista.length < 2 || parado) return;
    const t = setInterval(() => setVolta((v) => v + 1), 7_000);
    return () => clearInterval(t);
  }, [lista.length, parado]);

  if (lista.length === 0) return null;

  const gira = lista.length > 1;
  const encontro = lista[volta % lista.length]!;
  /* A mesma conta do cartão da agenda — nunca uma cópia dela. */
  const chamada = chamadaDoTempo(encontro, agora, meuFuso, t);
  const hora = formatarNoFuso(new Date(encontro.startsAt), meuFuso, locale);
  /** Perto = o conjunto em que `dias` é `null`. Decide o respiro, e só ele. */
  const perto = chamada.dias === null;
  const pleno = chamada.epoca === 'agora' || chamada.entradaAberta;
  /** A porta existe? Só então a caixa promete `Entrar`. */
  const porta = pleno ? encontro.joinUrl : null;

  /*
   * O rótulo leva o que a caixa cala — e leva a hora SEMPRE com a sigla, que é o
   * que faz a lei do fuso sobreviver ao telemóvel, onde a hora não se imprime.
   * `todos.length`, e não `lista.length`: dizer «e mais 2» quando há sete
   * marcados é a tela a inventar um número.
   */
  const rotulo =
    `Encontro: ${encontro.title} — ${chamada.palavra}, ` +
    `${hora.data} às ${hora.hora} ${hora.sigla}.` +
    (todos.length > 1 ? ` E mais ${todos.length - 1} na agenda.` : '');

  const laco = {
    'data-forma': 'pastilha-do-encontro',
    'data-peso': pleno ? 'pleno' : 'sereno',
    'data-epoca': chamada.epoca,
    'data-gira': gira ? 'sim' : 'nao',
    'data-respira': perto ? 'sim' : 'nao',
    'aria-label': rotulo,
    title: rotulo,
    onPointerEnter: () => setParado(true),
    onPointerLeave: () => setParado(false),
    onFocus: () => setParado(true),
    onBlur: () => setParado(false),
    /* 44 px de alvo — a altura de todo o resto da barra, e é por isso que o
       cabeçalho não cresce. O que se vê é o filho de 26 px. `hidden` até `xl`:
       ver o bloco «abaixo de 1280 px ela não cabe». */
    className: 'hidden min-h-[44px] shrink-0 items-center xl:inline-flex',
  };

  const caixa = (
    <span
      className="inline-flex min-h-[26px] items-center gap-1.5 px-2.5 py-1"
      style={{
        /* A caixa é o canal: é a única peça desta barra que tem uma. */
        background: 'color-mix(in srgb, var(--texto) 8%, transparent)',
        border: pleno
          ? '1.5px solid color-mix(in srgb, var(--destaque) 70%, var(--texto))'
          : '1px solid color-mix(in srgb, var(--texto) 32%, transparent)',
        borderRadius: '999px',
      }}
    >
      <Respiro perto={perto} />
      {/* `key` remonta o texto a cada volta, e é isso que faz o dissolve correr
          outra vez. Com um encontro `volta` nunca muda e a classe não sai. */}
      <span
        key={volta}
        className={`inline-flex items-baseline gap-1.5 whitespace-nowrap text-[11px] leading-4 ${gira ? 'vst-pastilha-troca' : ''}`}
      >
        <span className="font-semibold uppercase tracking-[0.07em]">{chamada.palavra}</span>
        {porta ? (
          <span className="font-semibold">· Entrar</span>
        ) : (
          /* A hora, com a sigla colada. **A DATA NÃO ENTRA** — ver o bloco
             «o que a caixa cala»: `FALTAM 5 DIAS` já diz o dia, e `30/09` ao
             lado dele é o mesmo facto duas vezes, a custo de 54 px na largura
             mais apertada em que esta pastilha existe. */
          <span
            data-forma="hora-da-pastilha"
            className="tabular-nums"
            style={{ color: 'var(--texto-suave)' }}
          >
            · {hora.hora} {hora.sigla}
          </span>
        )}
      </span>
    </span>
  );

  return porta ? (
    <a {...laco} href={porta} target="_blank" rel="noreferrer noopener">
      {caixa}
    </a>
  ) : (
    <Link {...laco} to={caminhoDaArea(areaSlug, `/agenda`)}>
      {caixa}
    </Link>
  );
}
